import { useState } from "react";
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dashboard from '@mui/icons-material/Dashboard';
import People from '@mui/icons-material/People';
import BarChart from '@mui/icons-material/BarChart';
import Settings from '@mui/icons-material/Settings';
import Close from '@mui/icons-material/Close';
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const MobileBottomNav = () => {
  const history = useHistory();
  const location = useLocation();
  const { permissions } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReportsPermission = permissions.includes('Read Reports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  const menuItems = {
    master: [
      { label: 'Company', route: '/CompanyEdit' },
      { label: 'Client', route: '/clientIndex' },
      { label: 'Vendor', route: '/vendorIndex' },
      { label: 'Account Head', route: '/accountHeadIndex' },
      { label: 'Bank', route: '/bankIndex' },
      { label: 'Category', route: '/categoryIndex' },
      { label: 'Item', route: '/itemIndex' },
    ],
    event: [
      { label: 'Product Enquiry', route: '/EnquiryIndex' },
      { label: 'Rental Enquiry', route: '/EventItemEnquiryIndex' },
      { label: 'Estimate', route: '/EstimateIndex' },
      { label: 'Delivery-Out', route: '/DeliveryIndex' },
      { label: 'Delivery-In', route: '/DeliveryInIndex' },
      { label: 'Rent Invoice', route: '/RentInvoiceIndex' },
    ],
    transaction: [
      { label: 'Purchase', route: '/PurchaseIndex' },
      { label: 'Sales', route: '/SaleIndex' },
    ],
    accounts: [
      { label: 'Payment Receipts', route: '/PaymentReceiptsIndex' },
      { label: 'Journal Entry', route: '/JournalEntryIndex' },
    ],
    reports: [
      { label: 'Stock', route: '/StockReportIndex' },
      { label: 'Item Forecast Report', route: '/ItemForecastReport' },
      { label: 'Estimate Report', route: '/MonthlyEstimateReport' },
      { label: 'Delivery Report', route: '/DeliverySummaryReport' },
      { label: 'Delivery Stock', route: '/DeliveryStockReport' },
      { label: 'Purchase Party & ItemWise Report', route: '/PurchasePartyandItemWiseReport' },
      { label: 'Sale Party & ItemWise Report', route: '/SalePartyandItemWiseReport' },
      { label: 'Rent Invoice Party & ItemWise Report', route: '/RentInvoicePartyandItemWiseReport' },
      { label: 'Ledger', route: '/LedgerReport' },
      { label: 'CashBook', route: '/CashBook' },
      { label: 'Customer Due List', route: '/CustomerDueListReport' },
      { label: 'Vendor Due List', route: '/VendorDueListReport' },
    ],
    settings: [
      { label: 'Users', route: '/UserIndex' },
      { label: 'Roles', route: '/RoleIndex' },
      { label: 'Reset Password', route: '/PasswordChange' },
    ],
  };

  const groups = [
    { id: 'master', label: 'Master data', allowed: hasReadMasterPermission },
    { id: 'event', label: 'Rentals', allowed: hasReadTransactionPermission || hasReadMasterPermission },
    { id: 'transaction', label: 'Transactions', allowed: hasReadTransactionPermission || hasReadMasterPermission },
    { id: 'accounts', label: 'Accounts', allowed: hasReadAccountsPermission },
    { id: 'reports', label: 'Reports', allowed: hasReadReportsPermission },
    { id: 'settings', label: 'Administration', allowed: hasReadAdministrationPermission },
  ].filter(group => group.allowed);
  const selectedGroup = groups.find(group => menuItems[group.id].some(item => item.route.toLowerCase() === location.pathname.toLowerCase()))?.id;
  const selectedTab = location.pathname === '/' || location.pathname.toLowerCase() === '/home'
    ? 'home' : ['event', 'reports'].includes(selectedGroup) ? selectedGroup : 'more';
  return (
    <>
      <Paper component="nav" aria-label="Mobile navigation" className="mobile-nav" elevation={0}>
        <BottomNavigation showLabels value={selectedTab} sx={{ background: 'transparent', height: 64 }}>
          <BottomNavigationAction value="home" label="Home" icon={<Dashboard />} onClick={() => handleNavigation('/Home')} />
          {groups.some(group => group.id === 'event') && <BottomNavigationAction value="event" label="Rentals" icon={<People />} onClick={event => handleMenuOpen(event, 'event')} />}
          {groups.some(group => group.id === 'reports') && <BottomNavigationAction value="reports" label="Reports" icon={<BarChart />} onClick={event => handleMenuOpen(event, 'reports')} />}
          <BottomNavigationAction value="more" label="More" icon={<Settings />} onClick={() => setMoreOpen(true)} />
        </BottomNavigation>
      </Paper>
      <Menu anchorEl={anchorEl} open={Boolean(activeMenu)} onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { mb: 1, width: 300, maxWidth: 'calc(100vw - 24px)', maxHeight: '65dvh', borderRadius: 3 } }}>
        {(menuItems[activeMenu] || []).map(item => <MenuItem key={item.route}
          selected={location.pathname.toLowerCase() === item.route.toLowerCase()}
          onClick={() => handleNavigation(item.route)} sx={{ whiteSpace: 'normal', py: 1.5 }}>{item.label}</MenuItem>)}
      </Menu>
      <Drawer anchor="bottom" open={moreOpen} onClose={() => setMoreOpen(false)}
        PaperProps={{ sx: { borderRadius: '24px 24px 0 0', maxHeight: '85dvh', pb: 'env(safe-area-inset-bottom)' } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">Your workspace</Typography>
          <IconButton aria-label="Close navigation" onClick={() => setMoreOpen(false)}><Close /></IconButton>
        </Box>
        <Box sx={{ overflowY: 'auto', p: 2 }}>
          {groups.map(group => <Box key={group.id} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.08em', mb: 1 }}>{group.label}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {menuItems[group.id].map(item => <Box component="button" type="button" key={item.route}
                onClick={() => { handleNavigation(item.route); setMoreOpen(false); }}
                sx={{ textAlign: 'left', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, minHeight: 48, fontSize: 14, color: 'text.primary', backgroundColor: location.pathname.toLowerCase() === item.route.toLowerCase() ? '#dff3ee' : '#f5f8f9', '&:hover': { backgroundColor: '#e8f3f0' } }}>
                {item.label}
              </Box>)}
            </Box>
          </Box>)}
          {!hasReadAdministrationPermission && <MenuItem onClick={() => { handleNavigation('/PasswordChange'); setMoreOpen(false); }}>Change password</MenuItem>}
        </Box>
      </Drawer>
    </>
  );
};
export default MobileBottomNav;
